import { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'

import CloseOutlinedIcon from '@ant-design/icons/CloseOutlined'
import SearchOutlinedIcon from '@ant-design/icons/SearchOutlined'

import Button from '@mui/material/Button'
import Skeleton from '@mui/material/Skeleton'
import Typography from '@components/_mui/Typography'

import { useQueryEvents } from '@shared/hooks/src/useQueryEvents'
import { getTestId, UserRole, userRoleToLabel } from '@shared/utils'

import useUser from '@hooks/useUser'
import SelectUser from '@components/SelectUser'

import { FilterPopper } from '../TableFilters.utils'

UserSelect.propTypes = {
  /** User role for selection, any system user when omitted */
  role: PropTypes.oneOf(Object.values(UserRole)),

  /** Label of the button */
  label: PropTypes.string.isRequired,

  /** Title of the popper */
  title: PropTypes.string,

  /** Callback fired when the value changes */
  onChange: PropTypes.func.isRequired,

  /** Dayjs value */
  value: PropTypes.number,
}

/**
 * Button opens a popper to select or manually search a user
 *
 * @example
 * <UserSelect label="Assigned User" value={value} onChange={handleChange} />
 */
export default function UserSelect({ role, label, title, onChange, value, ...other }) {
  const anchorRef = useRef(null)

  const [open, setOpen] = useState(false)
  const [user, setUser] = useState(null)

  const testId = getTestId(other, `user-select-${label}`)

  // Reset the user if the initialValue changes to empty
  useEffect(() => {
    if (!value && user) {
      setUser(null)
    }
  }, [value, user])

  const query = useUser(value, { enabled: Boolean(value) && !user })

  useQueryEvents(query, {
    onSuccess: (user) => setUser(user),
  })

  const handleToggle = () => setOpen((o) => !o)

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) return
    setOpen(false)
  }

  const handleSelect = (selectedUser) => {
    onChange(selectedUser?.id)
    setUser(selectedUser)
    setOpen(false)
  }

  return (
    <>
      <Button
        ref={anchorRef}
        color="inherit"
        endIcon={value ? <CloseOutlinedIcon /> : <SearchOutlinedIcon />}
        onClick={value ? () => onChange(undefined) : handleToggle}
        data-testid={`${testId}-button`}
        {...other}
      >
        {label}
        {(user || query.isFetching) && (
          <Typography
            component="span"
            noWrap
            sx={{
              color: 'primary.main',
              fontWeight: 'bold',
              textTransform: 'none',
            }}
          >
            {user ? `: ${user.fullName}` : <Skeleton width={80} sx={{ ml: 1 }} />}
          </Typography>
        )}
      </Button>
      <FilterPopper title={title} open={open} onClose={handleClose} anchorEl={anchorRef.current}>
        <SelectUser
          autoFocus
          openOnFocus
          placeholder={userRoleToLabel[role] ?? 'System user'}
          role={role}
          disabled={query.isFetching}
          loading={query.isFetching}
          value={user}
          onChange={handleSelect}
          data-testid={testId}
        />
      </FilterPopper>
    </>
  )
}
